/**
 * AIContentGenerator Component
 * Main component for AI content generation with prompt input, question type selector,
 * job progress tracking, and split-panel layout with resizable preview
 */

import React, {
    useState,
    useCallback,
    useMemo,
    useRef,
    useEffect,
    useImperativeHandle,
    forwardRef,
    type MouseEvent,
} from 'react';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import trim from 'lodash/trim';
import type {
    GenerateContentRequest,
    GenerateContentFromDocumentRequest,
    RegenerateContentRequest,
} from '@ai-content-hub/shared-api';
import type { JobDetailsSummary } from '@ai-content-hub/content-prompt';
import {
    useGenerationHistory,
    ChatLayout,
    type UploadedDocument,
    type ChatJobStatus,
} from '@ai-content-hub/content-prompt';
import type {
    AIContentGeneratorProps,
    AIContentGeneratorRef,
    AIGeneratorColors,
    PromptFormData,
} from './AIContentGenerator.types';
import { DEFAULT_COLORS } from './AIContentGenerator.types';
import { useJobTracking } from './useJobTracking';
import { PromptInput } from './PromptInput';
import { JobProgress } from './JobProgress';
import { QuestionTypeSelector } from './QuestionTypeSelector';
import { useAIContentTypes } from './useAIContentTypes';
import { SplitPanel, type SplitPanelColors } from '../common/SplitPanel';
import { useAIGeneratorTranslations } from './i18n';
import { ChatEditPanel, type ChatEditOutcome } from './ChatEditPanel';
import {
    assessChatEdit,
    buildChatEdit,
    questionSetItemCount,
    wantsWholeSetRegeneration,
    type ChatEditJobResult,
    type ChatEditMode,
    type ChatEditSnapshot,
} from './chatEdit';

export const AIContentGenerator = forwardRef<AIContentGeneratorRef, AIContentGeneratorProps>(
    function AIContentGenerator(
        {
            questionType: initialQuestionType,
            questionTypeLabel: questionTypeLabelProp,
            questionTypePlaceholder: questionTypePlaceholderProp,
            hideQuestionTypeSelector = false,
            hideQuickCreateUI = false,
            tenantId,
            contentId,
            storageKey,
            showPreview = false,
            previewContent,
            previewTitle,
            onRefreshPreview,
            onJobCreated,
            onJobCompleted,
            onError,
            pollingInterval,
            colors: colorsProp,
            className,
            promptFormComponent: PromptFormComponent,
            apiConfig,
            generationHistoryApi,
        },
        ref
    ) {
        // Merge colors with defaults
        const colors: AIGeneratorColors = useMemo(
            () => ({ ...DEFAULT_COLORS, ...colorsProp }),
            [colorsProp]
        );

        // Internal translations
        const t = useAIGeneratorTranslations();

        // Fetch AI content types via explicit calls
        const {
            options: questionTypeOptions,
            allOptions,
            questionTypeGroups,
            loading: qtLoading,
            error: qtError,
            fetchRegisteredOptions,
            fetchAllContentTypes,
        } = useAIContentTypes();

        useEffect(() => {
            fetchRegisteredOptions();
            fetchAllContentTypes();
        }, [fetchRegisteredOptions, fetchAllContentTypes]);

        // Local form state
        const [prompt, setPrompt] = useState('');
        const [questionType, setQuestionType] = useState(initialQuestionType || '');

        // Sync questionType when initialQuestionType prop changes (e.g., when content is loaded async)
        useEffect(() => {
            if (initialQuestionType) {
                setQuestionType(initialQuestionType);
            }
        }, [initialQuestionType]);

        // Fullscreen state for preview panel
        const [isFullscreen, setIsFullscreen] = useState(false);
        const previewPanelRef = useRef<HTMLDivElement>(null);
        /**
         * EAA-1981: last config submitted through the prompt form.
         *
         * Follow-up turns in the AI chat call the imperative `generate()`, which used to
         * send only { prompt, question_type, tenant_id }. The backend derives the media
         * catalog slug from `option.media` (mcq + media:image -> mcq_image), so dropping
         * `option` silently downgraded the request to a plain `mcq` and the follow-up
         * answer came back with no image and none of the chosen counts.
         */
        const lastFormConfigRef = useRef<Pick<
            PromptFormData,
            'option' | 'metadata'
        > | null>(null);

        // Job tracking hook
        const {
            jobId,
            jobStatus,
            isGenerating,
            isPolling,
            error,
            isRegeneration,
            startGeneration,
            startGenerationFromDocument,
            startRegeneration,
            trackJob,
            clearJob,
        } = useJobTracking({
            storageKey,
            pollingInterval,
            contentId,
            onJobCompleted: (job) => {
                onJobCompleted?.(job);
            },
            onJobFailed: (err) => {
                onError?.(err);
            },
        });

        // Generation history (chat mode)
        const history = useGenerationHistory(generationHistoryApi);

        // EAA-2029: targeted chat edit — which questions the next message may touch,
        // the content as it was before the pending edit (one-step undo), and what the
        // last edit did. Kept client-side only; the server also keeps an "AI sửa" version.
        const [chatTargets, setChatTargets] = useState<number[]>([]);
        const [chatItemCount, setChatItemCount] = useState(0);
        const [chatOutcome, setChatOutcome] = useState<ChatEditOutcome | null>(null);
        const [pendingRegenerate, setPendingRegenerate] = useState<{
            text: string;
            documents?: UploadedDocument[];
        } | null>(null);
        const [isUndoing, setIsUndoing] = useState(false);
        const chatEditRef = useRef<{
            jobId: string;
            text: string;
            snapshot: ChatEditSnapshot | null;
        } | null>(null);
        const undoSnapshotRef = useRef<ChatEditSnapshot | null>(null);

        const loadContentSnapshot = useCallback(
            async (id: string): Promise<ChatEditSnapshot | null> => {
                try {
                    const { contentService } = await import('@ai-content-hub/shared-api');
                    const c = await contentService.getContent(id);
                    return {
                        title: c.title,
                        data: (c.data ?? {}) as Record<string, unknown>,
                        config: (c.config ?? null) as Record<string, unknown> | null,
                    };
                } catch {
                    return null;
                }
            },
            []
        );

        // When an existing contentId is provided, seed the history hook so it can
        // fetch versions and auto-switch to chat mode if versions exist.
        useEffect(() => {
            if (contentId && generationHistoryApi && !history.rootContentId) {
                history.setRootContentId(contentId);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [contentId, generationHistoryApi]);

        // After a successful generation, capture root content_id and switch to chat
        const origOnJobCompleted = useRef(onJobCompleted);
        origOnJobCompleted.current = onJobCompleted;

        useEffect(() => {
            if (jobStatus?.status !== 'completed') return;
            const resultContentId = get(jobStatus, 'result.content_id') as string | undefined;
            if (resultContentId && generationHistoryApi) {
                if (!history.rootContentId) {
                    history.switchToChat(resultContentId);
                } else {
                    history.refreshHistory();
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [jobStatus?.status]);

        // If a chat-mode regeneration job fails, the "generating..." placeholder
        // appended in handleChatSend never gets replaced by refreshHistory()
        // (that only runs on 'completed') — resolve it to a failure message
        // instead of leaving it stuck saying "generating" forever.
        useEffect(() => {
            if (jobStatus?.status !== 'failed' || history.mode !== 'chat') return;
            history.resolvePendingGeneration(jobStatus.error);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [jobStatus?.status]);

        // Auto-dismiss "Generation complete" indicator in chat mode after 30s
        useEffect(() => {
            if (
                jobStatus?.status !== 'completed' ||
                !(history.mode === 'chat' && generationHistoryApi)
            )
                return;
            const id = setTimeout(() => clearJob(), 30000);
            return () => clearTimeout(id);
        }, [jobStatus?.status, history.mode, generationHistoryApi, clearJob]);

        // Handle question type selection (now only needs slug)
        const handleQuestionTypeChange = useCallback((slug: string) => {
            setQuestionType(slug);
        }, []);

        // Handle send/generate (or regenerate)
        const handleGenerate = useCallback(async () => {
            const trimmedPrompt = trim(prompt);

            if (isEmpty(trimmedPrompt)) {
                onError?.(t.errorEnterPrompt);
                return;
            }

            if (isEmpty(questionType)) {
                onError?.(t.errorSelectType);
                return;
            }

            try {
                if (isRegeneration && contentId) {
                    // Regenerate existing content
                    const request: RegenerateContentRequest = {
                        prompt: trimmedPrompt,
                        question_type: questionType,
                        tenant_id: tenantId || undefined,
                    };
                    await startRegeneration(request);
                } else {
                    // Generate new content
                    const request: GenerateContentRequest = {
                        prompt: trimmedPrompt,
                        question_type: questionType,
                        tenant_id: tenantId || undefined,
                    };
                    await startGeneration(request);
                }
                if (jobId) {
                    onJobCreated?.(jobId);
                }
            } catch (err) {
                const errorMsg = err instanceof Error ? err.message : 'Generation failed';
                onError?.(errorMsg);
            }
        }, [
            prompt,
            questionType,
            tenantId,
            contentId,
            isRegeneration,
            startGeneration,
            startRegeneration,
            jobId,
            onJobCreated,
            onError,
            t,
        ]);

        // Handle submit from injected prompt form component (content-kit PromptFormRenderer)
        const handlePromptFormSubmit = useCallback(
            async (data: PromptFormData) => {
                const trimmedPrompt = trim(data.prompt);

                if (isEmpty(trimmedPrompt) && !data.sourceDocument) {
                    onError?.(t.errorEnterPrompt);
                    return;
                }

                if (isEmpty(questionType)) {
                    onError?.(t.errorSelectType);
                    return;
                }

                if (
                    data.metadata?.source_type === 'document' &&
                    !data.sourceDocument
                ) {
                    onError?.(
                        'Please attach a worksheet file (.docx or .pdf) for document-based generation.'
                    );
                    return;
                }

                const effectivePrompt =
                    trimmedPrompt || data.sourceDocument?.name || '';

                // EAA-1981: remember the config so chat follow-ups can reuse it.
                lastFormConfigRef.current = {
                    option: data.option,
                    metadata: data.metadata,
                };

                try {
                    if (data.sourceDocument) {
                        if (isRegeneration) {
                            onError?.(
                                'Document-based regeneration is not supported yet. Remove the worksheet file or use prompt-only regeneration.'
                            );
                            return;
                        }
                        const docRequest: GenerateContentFromDocumentRequest = {
                            file: data.sourceDocument,
                            prompt: effectivePrompt,
                            question_type: questionType,
                            tenant_id: tenantId || undefined,
                            metadata: data.metadata,
                            ...(data.option !== undefined ? { option: data.option } : {}),
                            ...(data.status !== undefined ? { status: data.status } : {}),
                        };
                        await startGenerationFromDocument(docRequest);
                    } else if (isRegeneration && contentId) {
                        const request: RegenerateContentRequest = {
                            prompt: effectivePrompt,
                            question_type: questionType,
                            tenant_id: tenantId || undefined,
                            metadata: data.metadata,
                            ...(data.option !== undefined ? { option: data.option } : {}),
                        };
                        await startRegeneration(request);
                    } else {
                        const request: GenerateContentRequest = {
                            prompt: effectivePrompt,
                            question_type: questionType,
                            tenant_id: tenantId || undefined,
                            metadata: data.metadata,
                            ...(data.option !== undefined ? { option: data.option } : {}),
                            ...(data.status !== undefined ? { status: data.status } : {}),
                        };
                        await startGeneration(request);
                    }
                    if (jobId) {
                        onJobCreated?.(jobId);
                    }
                } catch (err) {
                    const errorMsg = err instanceof Error ? err.message : 'Generation failed';
                    onError?.(errorMsg);
                }
            },
            [
                questionType,
                tenantId,
                contentId,
                isRegeneration,
                startGeneration,
                startGenerationFromDocument,
                startRegeneration,
                jobId,
                onJobCreated,
                onError,
                t,
            ]
        );

        // Imperative API for parent (e.g. EDITOR_SAVE in embed)
        useImperativeHandle(
            ref,
            () => ({
                async generate(options?: { prompt?: string; questionType?: string }) {
                    const p = trim(options?.prompt ?? prompt);
                    const qt = options?.questionType ?? questionType;
                    if (isEmpty(p)) {
                        onError?.(t.errorEnterPrompt);
                        return;
                    }
                    if (isEmpty(qt)) {
                        onError?.(t.errorSelectType);
                        return;
                    }
                    // EAA-1981: carry the last form config into follow-up turns. Without
                    // `option` the backend cannot see media:image and answers with a plain
                    // mcq — the image and the chosen counts disappear mid-conversation.
                    const carried = lastFormConfigRef.current;
                    const carriedFields = {
                        ...(carried?.option !== undefined
                            ? { option: carried.option }
                            : {}),
                        ...(carried?.metadata !== undefined
                            ? { metadata: carried.metadata }
                            : {}),
                    };

                    try {
                        if (isRegeneration && contentId) {
                            await startRegeneration({
                                prompt: p,
                                question_type: qt,
                                tenant_id: tenantId || undefined,
                                ...carriedFields,
                            });
                        } else {
                            await startGeneration({
                                prompt: p,
                                question_type: qt,
                                tenant_id: tenantId || undefined,
                                ...carriedFields,
                            });
                        }
                        if (jobId) {
                            onJobCreated?.(jobId);
                        }
                    } catch (err) {
                        const errorMsg = err instanceof Error ? err.message : 'Generation failed';
                        onError?.(errorMsg);
                    }
                },
            }),
            [
                prompt,
                questionType,
                tenantId,
                contentId,
                isRegeneration,
                startGeneration,
                startRegeneration,
                jobId,
                onJobCreated,
                onError,
                t,
            ]
        );

        // Handle retry (for failed jobs)
        const handleRetry = useCallback(() => {
            clearJob();
        }, [clearJob]);

        // Handle sending a new message from the chat input (edits the saved set in place)
        const sendChatMessage = useCallback(
            async (text: string, documents: UploadedDocument[] | undefined, mode: ChatEditMode) => {
                const trimmedPrompt = trim(text);
                if (isEmpty(trimmedPrompt) && (!documents || documents.length === 0)) return;
                if (isEmpty(questionType)) {
                    onError?.(t.errorSelectType);
                    return;
                }

                const rootId = history.rootContentId;
                if (!rootId) return;

                // EAA-2029: a message that plainly asks to rebuild everything is confirmed
                // first — it replaces every question, which an edit never does.
                if (mode === 'patch' && chatTargets.length === 0 && wantsWholeSetRegeneration(trimmedPrompt)) {
                    setPendingRegenerate({ text, documents });
                    return;
                }
                setPendingRegenerate(null);
                setChatOutcome(null);

                const chatPrompt =
                    trimmedPrompt || (documents?.map((d) => d.name).join(', ') ?? '');
                const metadata: Record<string, unknown> = {
                    ...((lastFormConfigRef.current?.metadata as Record<string, unknown>) ?? {}),
                };
                if (documents && documents.length > 0) {
                    metadata.document_urls = documents.map((d) => d.url);
                    metadata.document_names = documents.map((d) => d.name);
                }
                const option = lastFormConfigRef.current?.option;

                try {
                    // EAA-2029: keep the pre-edit content for a one-step undo and for the
                    // before/after check once the job finishes.
                    const snapshot = await loadContentSnapshot(rootId);
                    const { generateService } = await import('@ai-content-hub/shared-api');
                    const request: RegenerateContentRequest = {
                        prompt: chatPrompt,
                        question_type: questionType,
                        tenant_id: tenantId || undefined,
                        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
                        ...(option !== undefined ? { option } : {}),
                        edit: buildChatEdit(mode, chatTargets),
                    };
                    const response = await generateService.regenerateContent(rootId, request);
                    chatEditRef.current = { jobId: response.job_id, text, snapshot };
                    history.appendGeneration(chatPrompt, metadata);
                    onJobCreated?.(response.job_id);
                    trackJob(response.job_id);
                } catch (err) {
                    const errorMsg = err instanceof Error ? err.message : 'Generation failed';
                    onError?.(errorMsg);
                }
            },
            [questionType, tenantId, history, onJobCreated, onError, trackJob, t, chatTargets, loadContentSnapshot]
        );

        const handleChatSend = useCallback(
            (text: string, documents?: UploadedDocument[]) => sendChatMessage(text, documents, 'patch'),
            [sendChatMessage]
        );

        // EAA-2029: keep the question chips in step with the saved set.
        useEffect(() => {
            const rootId = history.rootContentId;
            if (!rootId || history.mode !== 'chat') return;
            let cancelled = false;
            loadContentSnapshot(rootId).then((snap) => {
                if (!cancelled && snap) setChatItemCount(questionSetItemCount(snap.data));
            });
            return () => {
                cancelled = true;
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [history.rootContentId, history.mode, jobStatus?.status === 'completed']);

        // EAA-2029: after a chat edit completes, say what changed and warn when questions
        // disappeared or the language switched — with an undo to the pre-edit state.
        useEffect(() => {
            const pending = chatEditRef.current;
            if (!pending || !jobStatus || jobStatus.status !== 'completed') return;
            if (jobId !== pending.jobId) return;
            chatEditRef.current = null;
            const result = (jobStatus.result ?? {}) as ChatEditJobResult;
            if (result.regenerate_requested) {
                setPendingRegenerate({ text: pending.text });
                return;
            }
            const rootId = history.rootContentId;
            if (!rootId) return;
            loadContentSnapshot(rootId).then((after) => {
                const assessment =
                    pending.snapshot && after ? assessChatEdit(pending.snapshot.data, after.data) : null;
                undoSnapshotRef.current = pending.snapshot;
                setChatTargets([]);
                // A whole-set regeneration (confirmed, or the server's fallback for an
                // unsupported shape) reports no changed_items: every question is new.
                const changedItems =
                    result.edit_mode === 'patch'
                        ? (result.changed_items ?? [])
                        : Array.from({ length: assessment?.countAfter ?? 0 }, (_, i) => i);
                setChatOutcome({
                    changedItems,
                    rejected: result.rejected ?? [],
                    assessment,
                    canUndo: !!pending.snapshot && !!assessment?.changed,
                });
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [jobStatus?.status, jobId]);

        const handleUndoChatEdit = useCallback(async () => {
            const rootId = history.rootContentId;
            const snap = undoSnapshotRef.current;
            if (!rootId || !snap) return;
            setIsUndoing(true);
            try {
                const { contentService } = await import('@ai-content-hub/shared-api');
                await contentService.updateContent(rootId, {
                    title: snap.title,
                    data: snap.data,
                    config: snap.config ?? undefined,
                });
                undoSnapshotRef.current = null;
                setChatOutcome((o) => (o ? { ...o, canUndo: false, notice: 'undone' } : o));
                onRefreshPreview?.();
                history.refreshHistory();
            } catch {
                setChatOutcome((o) => (o ? { ...o, notice: 'undoFailed' } : o));
            } finally {
                setIsUndoing(false);
            }
        }, [history, onRefreshPreview]);

        const handleToggleChatTarget = useCallback((index: number | null) => {
            setChatTargets((prev) =>
                index === null
                    ? []
                    : prev.includes(index)
                      ? prev.filter((i) => i !== index)
                      : [...prev, index]
            );
        }, []);

        // Handle dismiss (clear job and optionally form)
        const handleDismiss = useCallback(() => {
            clearJob();
            if (jobStatus?.status === 'completed') {
                setPrompt('');
            }
        }, [clearJob, jobStatus]);

        // Fullscreen handling
        const handleFullscreenToggle = useCallback(async (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (!previewPanelRef.current) return;

            try {
                if (!document.fullscreenElement) {
                    await previewPanelRef.current.requestFullscreen();
                } else {
                    await document.exitFullscreen();
                }
            } catch (err) {
                console.error('Fullscreen error:', err);
            }
        }, []);

        // Listen for fullscreen changes
        useEffect(() => {
            const handleFullscreenChange = () => {
                setIsFullscreen(!!document.fullscreenElement);
            };

            document.addEventListener('fullscreenchange', handleFullscreenChange);
            document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

            return () => {
                document.removeEventListener('fullscreenchange', handleFullscreenChange);
                document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            };
        }, []);

        // Track previous job status to detect completion
        const prevJobStatusRef = useRef<string | null>(null);

        // Auto-refresh preview when job completes (for regeneration)
        useEffect(() => {
            const currentStatus = jobStatus?.status || null;
            const prevStatus = prevJobStatusRef.current;
            // Record the transition immediately — the early `return` below (for
            // the setTimeout cleanup) used to skip this line entirely, so
            // prevJobStatusRef never actually recorded 'completed'.
            prevJobStatusRef.current = currentStatus;

            // Detect transition to 'completed' status
            if (
                currentStatus === 'completed' &&
                prevStatus !== 'completed' &&
                prevStatus !== null
            ) {
                // Job just completed - trigger preview refresh after a short delay
                // to allow backend to finish saving the content
                const timer = setTimeout(() => {
                    onRefreshPreview?.();
                }, 500);
                return () => clearTimeout(timer);
            }
        }, [jobStatus?.status, onRefreshPreview]);

        // Determine if we should show the form or just progress.
        // When using PromptFormComponent, always show form so JobDetailsBar (below prompt) stays visible.
        const showForm =
            !jobId ||
            jobStatus?.status === 'completed' ||
            jobStatus?.status === 'failed' ||
            jobStatus?.status === 'cancelled' ||
            !!PromptFormComponent;
        const showJobProgress = !!jobId && !!jobStatus;

        // Map JobDetail to JobDetailsSummary for prompt forms (content-prompt stays independent of shared-api)
        const jobStatusSummary: JobDetailsSummary | null = useMemo(() => {
            if (!jobStatus) return null;
            return {
                status: jobStatus.status,
                error: jobStatus.error || undefined,
                progress: jobStatus.progress
                    ? {
                          percentage: jobStatus.progress.percentage,
                          completed_steps: jobStatus.progress.completed_steps,
                          total_steps: jobStatus.progress.total_steps,
                      }
                    : undefined,
                duration_seconds: jobStatus.duration_seconds,
            };
        }, [jobStatus]);

        // Map AI Generator colors to SplitPanel colors
        const splitPanelColors: SplitPanelColors = useMemo(
            () => ({
                dividerBackground: colors.border,
                dividerActive: colors.primary,
                handleColor: colors.textMuted,
                handleActiveColor: 'white',
            }),
            [colors.border, colors.primary, colors.textMuted]
        );

        const handleChatRollback = useCallback(
            async (version: number) => {
                const res = await history.handleRollback(version);
                if (res?.job_id) {
                    trackJob(res.job_id);
                    return;
                }
                // EAA-2029: ↺ used to fail silently ("bấm vào không có tác dụng gì").
                setChatOutcome({
                    changedItems: [],
                    rejected: [],
                    assessment: null,
                    canUndo: false,
                    notice: 'rollbackFailed',
                });
            },
            [history, trackJob]
        );

        const isChatMode = history.mode === 'chat' && generationHistoryApi;

        const chatJobStatus: ChatJobStatus | null = useMemo(() => {
            if (!jobStatus || !isChatMode) return null;
            const s = jobStatus.status;
            if (s !== 'pending' && s !== 'running' && s !== 'completed' && s !== 'failed')
                return null;
            return {
                status: s,
                progress: jobStatus.progress
                    ? {
                          percentage: jobStatus.progress.percentage,
                          completed_steps: jobStatus.progress.completed_steps,
                          total_steps: jobStatus.progress.total_steps,
                      }
                    : undefined,
                error: jobStatus.error || undefined,
            };
        }, [jobStatus, isChatMode]);

        // Render the AI form content (left panel)
        const renderFormContent = () => {
            if (isChatMode) {
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                        <ChatEditPanel
                            itemCount={chatItemCount}
                            selected={chatTargets}
                            onToggle={handleToggleChatTarget}
                            outcome={chatOutcome}
                            onUndo={handleUndoChatEdit}
                            undoing={isUndoing}
                            onDismiss={() => setChatOutcome(null)}
                            pendingRegenerate={!!pendingRegenerate}
                            onConfirmRegenerate={() => {
                                const p = pendingRegenerate;
                                if (p) void sendChatMessage(p.text, p.documents, 'regenerate');
                            }}
                            onCancelRegenerate={() => setPendingRegenerate(null)}
                            disabled={isGenerating}
                            colors={colors}
                            t={t}
                        />
                        <div style={{ flex: 1, minHeight: 0 }}>
                            <ChatLayout
                                messages={history.messages}
                                onSendMessage={handleChatSend}
                                onRollback={handleChatRollback}
                                isRollbackLoading={history.isRollbackLoading || isGenerating}
                                loading={isGenerating}
                                disabled={isGenerating}
                                apiConfig={apiConfig}
                                className="h-full"
                                jobStatus={chatJobStatus}
                            />
                        </div>
                    </div>
                );
            }

            return (
                <div
                    className="ai-generator-form-content"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24,
                        background: colors.background,
                        height: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    {/* Header */}
                    {/* <div
        className="ai-generator-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          paddingBottom: 16,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <div
          className="ai-generator-icon-wrapper"
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            background: `${colors.primary}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            className="ai-generator-icon"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke={colors.primary}
            strokeWidth="2"
          >
            <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a2 2 0 0 1 0 4h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a2 2 0 0 1 0-4h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
            <path d="M9 14v4" />
            <path d="M15 14v4" />
          </svg>
        </div>
        <h3
          className="ai-generator-title"
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 600,
            color: colors.text,
          }}
        >
          {t.title}
        </h3>
      </div> */}

                    {/* Error display */}
                    {error && !showJobProgress && (
                        <div
                            className="ai-generator-error"
                            style={{
                                padding: 16,
                                background: `${colors.error}10`,
                                borderRadius: 8,
                                border: `1px solid ${colors.error}30`,
                                fontSize: 14,
                                color: colors.error,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 16,
                            }}
                        >
                            <span className="ai-generator-error-message">{error}</span>
                            <button
                                className="ai-generator-error-dismiss"
                                onClick={() => clearJob()}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: colors.error,
                                    cursor: 'pointer',
                                    fontSize: 18,
                                    padding: 4,
                                    lineHeight: 1,
                                    borderRadius: 4,
                                    transition: 'background 200ms',
                                }}
                            >
                                ×
                            </button>
                        </div>
                    )}

                    {/* Form section */}
                    {showForm && (
                        <div
                            className="ai-generator-form"
                            style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
                        >
                            {/* Question Type Selector (hidden when controlled from parent, e.g. header) */}
                            {!hideQuestionTypeSelector && (
                                <div
                                    className="ai-generator-field ai-generator-field--question-type"
                                    style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                                >
                                    <label
                                        className="ai-generator-label"
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: colors.text,
                                        }}
                                    >
                                        {questionTypeLabelProp ?? t.questionTypeLabel}
                                    </label>
                                    <QuestionTypeSelector
                                        value={questionType}
                                        onChange={handleQuestionTypeChange}
                                        disabled={isGenerating}
                                        placeholder={
                                            questionTypePlaceholderProp ?? t.questionTypePlaceholder
                                        }
                                        colors={colors}
                                        options={questionTypeOptions}
                                        loading={qtLoading}
                                        error={qtError}
                                    />
                                </div>
                            )}

                            {/* Prompt form: injected component or built-in PromptInput */}
                            {PromptFormComponent ? (
                                <PromptFormComponent
                                    questionType={questionType}
                                    onSubmit={handlePromptFormSubmit}
                                    loading={isGenerating}
                                    disabled={isGenerating || isEmpty(questionType)}
                                    isRegeneration={isRegeneration}
                                    apiConfig={apiConfig}
                                    questionTypeOptions={questionTypeOptions}
                                    allQuestionTypeOptions={allOptions}
                                    questionTypeGroups={questionTypeGroups}
                                    onQuestionTypeChange={handleQuestionTypeChange}
                                    hideQuickCreateUI={hideQuickCreateUI}
                                    jobStatus={jobStatusSummary}
                                    onRetry={handleRetry}
                                    onDismiss={handleDismiss}
                                />
                            ) : (
                                <div
                                    className="ai-generator-field ai-generator-field--prompt"
                                    style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                                >
                                    <label
                                        className="ai-generator-label"
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: colors.text,
                                        }}
                                    >
                                        {t.promptLabel}
                                    </label>
                                    <PromptInput
                                        value={prompt}
                                        onChange={setPrompt}
                                        onSend={handleGenerate}
                                        disabled={isGenerating || isEmpty(questionType)}
                                        loading={isGenerating}
                                        isRegeneration={isRegeneration}
                                        placeholder={
                                            !isEmpty(questionType)
                                                ? t.promptPlaceholder
                                                : t.promptPlaceholderDisabled
                                        }
                                        colors={colors}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Job Progress: only when no custom form (forms embed JobDetailsBar inline) */}
                    {showJobProgress && !PromptFormComponent && (
                        <div className="ai-generator-progress">
                            <JobProgress
                                jobStatus={jobStatus}
                                isPolling={isPolling}
                                onRetry={handleRetry}
                                onDismiss={handleDismiss}
                                colors={colors}
                            />
                        </div>
                    )}

                    {/* Tips section */}
                    {/* {!showJobProgress && (
        <div
          className="ai-generator-tips"
          style={{
            padding: 16,
            background: `${colors.info}08`,
            borderRadius: 8,
            border: `1px solid ${colors.info}20`,
            fontSize: 13,
            color: colors.info,
          }}
        >
          <strong style={{ display: 'block', marginBottom: 8 }}>{t.tipsTitle}</strong>
          <ul style={{ margin: 0, paddingLeft: 24, lineHeight: 1.6 }}>
            <li>{t.tipSpecific}</li>
            <li>{t.tipDifficulty}</li>
            <li>{t.tipShortcut}</li>
          </ul>
        </div>
      )} */}
                </div>
            );
        };

        // Render preview content (right panel)
        const renderPreviewContent = () => (
            <div
                ref={previewPanelRef}
                className="ai-generator-preview-panel"
                style={{
                    height: '100%',
                    background: isFullscreen ? '#fafafa' : colors.surface,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {/* Preview Header */}
                <div
                    className="ai-generator-preview-header"
                    style={{
                        padding: '8px 16px',
                        borderBottom: `1px solid ${colors.border}`,
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: colors.background,
                        flexShrink: 0,
                    }}
                >
                    <span
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: colors.text,
                        }}
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                        </svg>
                        {previewTitle || t.previewTitle || 'Preview'}
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {/* Fullscreen button */}
                        <button
                            onClick={handleFullscreenToggle}
                            style={{
                                background: 'none',
                                border: `1px solid ${colors.border}`,
                                borderRadius: 4,
                                padding: '4px 8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: 12,
                                color: colors.textMuted,
                                transition: 'all 200ms',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = colors.primary;
                                e.currentTarget.style.color = colors.primary;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = colors.border;
                                e.currentTarget.style.color = colors.textMuted;
                            }}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                {isFullscreen ? (
                                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                                ) : (
                                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                                )}
                            </svg>
                            {isFullscreen ? t.exitFullscreen : t.fullscreen}
                        </button>
                        {/* Refresh button */}
                        {onRefreshPreview && (
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onRefreshPreview();
                                }}
                                style={{
                                    background: 'none',
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: 4,
                                    padding: '4px 8px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontSize: 12,
                                    color: colors.textMuted,
                                    transition: 'all 200ms',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = colors.primary;
                                    e.currentTarget.style.color = colors.primary;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = colors.border;
                                    e.currentTarget.style.color = colors.textMuted;
                                }}
                            >
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                </svg>
                                {t.refresh}
                            </button>
                        )}
                    </div>
                </div>

                {/* Preview Content */}
                <div
                    className="ai-generator-preview-content"
                    style={{
                        flex: 1,
                        overflow: 'auto',
                        padding: isFullscreen ? 24 : 0,
                    }}
                >
                    {previewContent ? (
                        previewContent
                    ) : (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: colors.textMuted,
                                flexDirection: 'column',
                                gap: 8,
                            }}
                        >
                            <svg
                                width="48"
                                height="48"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1"
                                opacity={0.5}
                            >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                            </svg>
                            <span>
                                {t.previewPlaceholder ||
                                    'Preview will appear here after content is loaded'}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );

        const shouldShowPreview = showPreview || (isChatMode && !!previewContent);

        return (
            <SplitPanel
                leftContent={renderFormContent()}
                rightContent={renderPreviewContent()}
                showRight={shouldShowPreview}
                classPrefix="ai-generator"
                colors={splitPanelColors}
                className={className}
                style={{ background: colors.background }}
                initialLeftWidth={30}
            />
        );
    }
);

export default AIContentGenerator;
